import { SpanStatusCode } from '@opentelemetry/api';
import * as Sentry from '@sentry/nextjs';
import BotImage from 'components/botimage';
import Layout from 'components/layout';
import BuyPremium from 'components/premium/BuyPremium';
import { PremiumFeaturesTable } from 'components/premium/FeaturesTable';
import PremiumFlag from 'components/premium/MultiplierFlag';
import Anchor from 'components/typography/anchor';
import lovebot from 'images/bots/lovebot.svg';
import premiumShareImg from 'images/shares/premium.png';
import { otelApi } from 'lib/instrumentation'; // Need this to get the instrumentation initialized
import {
  getExistingCustomer,
  getResistbotPrices,
  RECURRING_PERIODS,
} from 'lib/stripe';
import PropTypes from 'prop-types';
import {
  BankAccountPropType,
  CardPaymentMethodPropType,
  CashAppPropType,
  LinkPropType,
  PricesPropType,
} from 'utilities/propTypes';
import { auth } from '@/auth';

const Premium = ({ prices, customer, paymentMethod, clientSecret }) => {
  return (
    <Layout
      title="Resistbot Premium"
      description="Support Resistbot and supercharge your organizing."
      image={premiumShareImg}
      imageAltText='Illustration of a robot with heart eyes with overlay text that reads, "Upgrade to Premium: AI writer, priority access, more daily letters, personal keyword, text blasts to followers, exclusive pin, bill alerts, and daily front pages. Text premium to 50409."'
    >
      <div className="text-center text-sm">
        <BotImage src={lovebot.src} />
        <h1>Upgrade to Premium</h1>
        <ul className="mb-4 flex list-inside list-disc flex-wrap justify-center gap-x-2.5 marker:text-stone-300 dark:marker:text-stone-600">
          <li className="list-none">AI writer</li>
          <li>Fax &amp; mail&nbsp;letters</li>
          <li>
            <PremiumFlag /> coins per dollar
          </li>
          <li>Creator&nbsp;earnings</li>
          <li>Limited-edition pin (yearly&nbsp;only)</li>
          <li>Daily newspaper front&nbsp;pages</li>
          <li>Custom&nbsp;keyword</li>
          <li>
            <Anchor className="anchor-subtle" href="#plans">
              And much more
            </Anchor>
          </li>
        </ul>
      </div>

      <BuyPremium
        prices={prices}
        customer={customer}
        paymentMethod={paymentMethod}
        clientSecret={clientSecret}
      />

      <PremiumFeaturesTable prices={prices} />
    </Layout>
  );
};

export const getServerSideProps = Sentry.wrapGetServerSidePropsWithSentry(
  async (ctx) => {
    const activeSpan = otelApi.trace.getActiveSpan();

    const session = await auth(ctx);
    const user = session?.user;
    const userId = user?.id;
    const stripeCustomerId = user?.stripe?.customerId;

    Sentry.addBreadcrumb({
      category: 'GSSP:/premium',
      message: 'Incoming data',
      data: { user: session?.user },
    });

    activeSpan.setAttributes({
      contact_uuid: userId,
    });
    Sentry.setUser({ id: userId });

    const details = await Promise.all([
      getResistbotPrices({ uuid: userId }),
      getExistingCustomer({ uuid: userId, customerId: stripeCustomerId }),
    ]);

    const prices = details[0];
    const { customer, paymentMethod, setupIntent, attempts } = details[1];

    Sentry.addBreadcrumb({
      category: 'GSSP:/premium',
      message: 'Located customer details',
      data: {
        customer,
        paymentMethod,
        setupIntent,
        traceId: activeSpan.spanContext().traceId,
      },
    });

    let sentryId = null;

    if (userId) {
      if (!customer) {
        const errorMessage = 'No Stripe Customer found for user';
        sentryId = Sentry.captureException(
          new Error('No Stripe Customer found for user')
        );
        activeSpan.setStatus({
          code: SpanStatusCode.ERROR,
          message: errorMessage,
        });
      }
    }

    activeSpan.setAttributes({
      'stripe.customer.id': stripeCustomerId || customer?.id,
      'stripe.paymentMethod': paymentMethod?.id,
      'stripe.setupIntent': setupIntent?.id,
      'stripe.should_collect_payment_method': setupIntent?.clientSecret,
      'stripe.search.attempts': attempts.search.attempts || null,
      'stripe.search.elapsedTime': attempts.search.time || null,
      'stripe.direct.attempts': attempts.direct.attempts || null,
      'stripe.direct.elapsedTime': attempts.direct.time || null,
      'stripe.linked': customer ? true : null,
      'sentry.error': sentryId,
    });

    return {
      props: {
        prices,
        customer,
        paymentMethod,
        clientSecret: setupIntent.clientSecret,
        sentryId,
      },
    };
  }
);

Premium.propTypes = {
  prices: PricesPropType,
  customer: PropTypes.shape({
    id: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    uuid: PropTypes.string.isRequired,
    subscription: PropTypes.shape({
      id: PropTypes.string.isRequired,
      active: PropTypes.bool.isRequired,
      plan: PropTypes.shape({
        id: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired,
        amountFormatted: PropTypes.string.isRequired,
        interval: PropTypes.oneOf([
          RECURRING_PERIODS.MONTH,
          RECURRING_PERIODS.YEAR,
        ]).isRequired,
      }).isRequired,
    }),
    billingPortalSession: PropTypes.string,
  }),
  paymentMethod: PropTypes.oneOfType([
    CardPaymentMethodPropType,
    BankAccountPropType,
    CashAppPropType,
    LinkPropType,
  ]),
  clientSecret: PropTypes.string,
};

export default Premium;
